import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';
import { GuideStatCard } from '@/features/guide/components/GuideStatCard';
import {
  useCreateBankAccount,
  useCreateWithdrawal,
  useDeleteBankAccount,
  useGuideBankAccounts,
  useGuideTransactions,
  useGuideWalletSummary,
  useGuideWithdrawals,
  useSetPrimaryBankAccount,
} from '@/features/guide-finance/hooks/useGuideFinance';
import { formatCurrency } from '@/features/guide-finance/utils/formatCurrency';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { dashboardLightHeroSx } from '@/shared/styles/surfaces';
import {
  formatStatusLabel,
  walletTransactionStatusToBadge,
  withdrawalStatusToBadge,
} from '@/shared/utils/statusBadge';

const walletTabs = ['transactions', 'withdrawals', 'bank-accounts'] as const;
type WalletTabKey = (typeof walletTabs)[number];

const withdrawalSchema = z.object({
  amount: z.coerce.number().positive('Amount must be greater than zero'),
  bankAccountId: z.string().min(1, 'Select a bank account'),
  remarks: z.string().max(500).optional(),
});

const bankAccountSchema = z.object({
  accountHolderName: z.string().min(2, 'Enter account holder name'),
  accountNumber: z.string().min(6, 'Enter a valid account number'),
  ifscCode: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/i, 'Invalid IFSC code'),
  bankName: z.string().min(2, 'Enter bank name'),
  branch: z.string().optional(),
  upiId: z.string().optional(),
});

type WithdrawalFormValues = z.infer<typeof withdrawalSchema>;
type BankAccountFormValues = z.infer<typeof bankAccountSchema>;

function getWalletTab(tab: string | null): WalletTabKey {
  return walletTabs.includes(tab as WalletTabKey) ? (tab as WalletTabKey) : 'transactions';
}

function prettifyEnum(value: string) {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function GuideWalletPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getWalletTab(searchParams.get('tab'));
  const [transactionsPage, setTransactionsPage] = useState(0);
  const [transactionsRowsPerPage, setTransactionsRowsPerPage] = useState(10);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);

  const summaryQuery = useGuideWalletSummary();
  const transactionsQuery = useGuideTransactions(
    { page: transactionsPage + 1, limit: transactionsRowsPerPage },
    { enabled: activeTab === 'transactions' }
  );
  const bankAccountsQuery = useGuideBankAccounts({
    enabled: activeTab === 'withdrawals' || activeTab === 'bank-accounts',
  });
  const withdrawalsQuery = useGuideWithdrawals(
    { page: 1, limit: 20 },
    { enabled: activeTab === 'withdrawals' }
  );
  const createWithdrawal = useCreateWithdrawal();
  const createBankAccount = useCreateBankAccount();
  const setPrimaryBankAccount = useSetPrimaryBankAccount();
  const deleteBankAccount = useDeleteBankAccount();

  const {
    register: registerWithdrawal,
    handleSubmit: handleWithdrawalSubmit,
    reset: resetWithdrawalForm,
    formState: { errors: withdrawalErrors },
  } = useForm<WithdrawalFormValues>({
    resolver: zodResolver(withdrawalSchema),
    defaultValues: { bankAccountId: '', remarks: '' },
  });

  const {
    register: registerBankAccount,
    handleSubmit: handleBankAccountSubmit,
    reset: resetBankAccountForm,
    formState: { errors: bankAccountErrors },
  } = useForm<BankAccountFormValues>({
    resolver: zodResolver(bankAccountSchema),
    defaultValues: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branch: '',
      upiId: '',
    },
  });

  const wallet = summaryQuery.data?.wallet;
  const commercial = summaryQuery.data?.commercial;
  const currency = wallet?.currency ?? 'INR';
  const transactions = transactionsQuery.data?.data ?? [];
  const withdrawals = withdrawalsQuery.data?.data ?? [];
  const accounts = bankAccountsQuery.data ?? [];
  const withdrawableAccounts = useMemo(
    () => accounts.filter((account) => account.verificationStatus === 'VERIFIED'),
    [accounts]
  );
  const manageableAccounts = useMemo(
    () => accounts.filter((account) => account.verificationStatus !== 'FAILED'),
    [accounts]
  );

  const handleTabChange = (_: React.SyntheticEvent, value: WalletTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const onSubmitWithdrawal = handleWithdrawalSubmit(async (values) => {
    await createWithdrawal.mutateAsync(values);
    resetWithdrawalForm();
    setShowWithdrawalForm(false);
  });

  const onSubmitBankAccount = handleBankAccountSubmit(async (values) => {
    await createBankAccount.mutateAsync({
      ...values,
      ifscCode: values.ifscCode.toUpperCase(),
      isPrimary: accounts.length === 0,
    });
    resetBankAccountForm();
    setShowBankAccountForm(false);
  });

  if (summaryQuery.isLoading && !wallet) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (summaryQuery.error || !wallet) {
    return (
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Alert severity="error">Failed to load wallet. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pt: { xs: 0, md: 0 }, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            ...dashboardLightHeroSx,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <GuidePageHeader
            title="Wallet"
            subtitle="Track ledger activity, request withdrawals, and manage payout accounts without leaving this page."
            actions={
              <Button
                variant="contained"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('tab', 'withdrawals');
                  setSearchParams(next, { replace: true });
                }}
                disabled={!wallet.availableBalance}
                sx={{
                  borderRadius: 99,
                  px: 2.25,
                  boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
                }}
              >
                Withdraw
              </Button>
            }
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <GuideStatCard
                label="Available"
                value={formatCurrency(wallet.availableBalance, currency)}
                icon={<AccountBalanceWalletIcon />}
                accent="success.main"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <GuideStatCard
                label="Pending"
                value={formatCurrency(wallet.pendingBalance, currency)}
                subtitle="Releases after trip completion"
                icon={<HourglassTopIcon />}
                accent="warning.main"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <GuideStatCard
                label="Withdrawn"
                value={formatCurrency(wallet.withdrawnBalance, currency)}
                icon={<SavingsIcon />}
                accent="primary.main"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <GuideStatCard
                label="Lifetime"
                value={formatCurrency(wallet.totalLifetimeEarnings, currency)}
                icon={<TrendingUpIcon />}
                accent="secondary.main"
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pb: 3 }}>
        {wallet.pendingBalance === 0 && wallet.availableBalance === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Earnings move from <strong>Pending</strong> to <strong>Available</strong> after a trip
            is completed and confirmed.
          </Alert>
        )}

        {commercial && (
          <Paper
            elevation={0}
            sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Commercial agreement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Platform commission:{' '}
              <strong>
                {commercial.commissionType === 'PERCENTAGE'
                  ? `${commercial.commissionValue}%`
                  : formatCurrency(commercial.commissionValue, currency)}
              </strong>{' '}
              per booking
            </Typography>
          </Paper>
        )}

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2, pt: 1.5 }}
          >
            <Tab label="Transactions" value="transactions" />
            <Tab label="Withdrawals" value="withdrawals" />
            <Tab label="Bank accounts" value="bank-accounts" />
          </Tabs>
          <Divider />

          {activeTab === 'transactions' && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                Wallet ledger
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Every credit and debit appears here in one place.
              </Typography>

              {transactionsQuery.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              )}

              {transactionsQuery.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to load transactions.
                </Alert>
              )}

              {!transactionsQuery.isLoading && !transactions.length && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Typography color="text.secondary">
                    No transactions yet. Completed bookings and wallet activity will appear here.
                  </Typography>
                </Paper>
              )}

              {transactions.map((transaction) => (
                <Paper
                  key={transaction.id}
                  elevation={0}
                  sx={{
                    p: 2.25,
                    mb: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>
                        {prettifyEnum(transaction.transactionType)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {prettifyEnum(transaction.referenceType)}
                        {transaction.bookingId ? ` · Booking ${transaction.bookingId.slice(-6)}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {new Date(transaction.createdAt).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                      <Typography
                        fontWeight={800}
                        color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(transaction.amount, currency)}
                      </Typography>
                      <StatusBadge
                        status={walletTransactionStatusToBadge(transaction.status)}
                        label={formatStatusLabel(transaction.status)}
                        sx={{ mt: 0.75 }}
                      />
                    </Box>
                  </Box>
                </Paper>
              ))}

              {transactionsQuery.data && (
                <TablePagination
                  component="div"
                  count={transactionsQuery.data.pagination.total}
                  page={transactionsPage}
                  onPageChange={(_, page) => setTransactionsPage(page)}
                  rowsPerPage={transactionsRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setTransactionsRowsPerPage(parseInt(event.target.value, 10));
                    setTransactionsPage(0);
                  }}
                />
              )}
            </Box>
          )}

          {activeTab === 'withdrawals' && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <GuidePageHeader
                title="Withdrawals"
                subtitle="Request payout from your available balance. Admin approval is still required."
                actions={
                  <Button variant="contained" onClick={() => setShowWithdrawalForm((value) => !value)}>
                    {showWithdrawalForm ? 'Cancel' : 'New withdrawal'}
                  </Button>
                }
              />

              <Alert severity="info" sx={{ mb: 2.5 }}>
                Available balance: <strong>{formatCurrency(wallet.availableBalance, currency)}</strong>
              </Alert>

              {showWithdrawalForm && (
                <Paper
                  component="form"
                  onSubmit={onSubmitWithdrawal}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  {bankAccountsQuery.isLoading ? (
                    <CircularProgress size={24} />
                  ) : !withdrawableAccounts.length ? (
                    <Alert severity="warning">
                      {accounts.length
                        ? 'Bank accounts must be verified by admin before you can withdraw. Check the Bank accounts tab for status.'
                        : 'Add a bank account before requesting a withdrawal.'}
                    </Alert>
                  ) : (
                    <>
                      <TextField
                        {...registerWithdrawal('amount')}
                        label="Amount"
                        type="number"
                        fullWidth
                        margin="normal"
                        error={!!withdrawalErrors.amount}
                        helperText={withdrawalErrors.amount?.message}
                      />
                      <TextField
                        {...registerWithdrawal('bankAccountId')}
                        select
                        label="Bank account"
                        fullWidth
                        margin="normal"
                        defaultValue=""
                        error={!!withdrawalErrors.bankAccountId}
                        helperText={withdrawalErrors.bankAccountId?.message}
                      >
                        {withdrawableAccounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.bankName} · {account.accountNumberMasked}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        {...registerWithdrawal('remarks')}
                        label="Remarks (optional)"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={2}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={createWithdrawal.isPending}
                        sx={{ mt: 2 }}
                      >
                        Submit request
                      </Button>
                    </>
                  )}
                </Paper>
              )}

              {withdrawalsQuery.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !withdrawals.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Typography color="text.secondary">No withdrawal requests yet.</Typography>
                </Paper>
              ) : (
                withdrawals.map((withdrawal) => (
                  <Paper
                    key={withdrawal.id}
                    elevation={0}
                    sx={{
                      p: 2.25,
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box>
                        <Typography fontWeight={700}>
                          {formatCurrency(withdrawal.amount, withdrawal.currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Requested on {new Date(withdrawal.requestedAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <StatusBadge
                        status={withdrawalStatusToBadge(withdrawal.status)}
                        label={formatStatusLabel(withdrawal.status)}
                      />
                    </Box>
                    {withdrawal.adminRemarks && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Admin: {withdrawal.adminRemarks}
                      </Typography>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          )}

          {activeTab === 'bank-accounts' && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <GuidePageHeader
                title="Bank Accounts"
                subtitle="Add payout accounts and keep your primary account ready for withdrawals."
                actions={
                  <Button variant="contained" onClick={() => setShowBankAccountForm((value) => !value)}>
                    {showBankAccountForm ? 'Cancel' : 'Add account'}
                  </Button>
                }
              />

              {showBankAccountForm && (
                <Paper
                  component="form"
                  onSubmit={onSubmitBankAccount}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...registerBankAccount('accountHolderName')}
                        label="Account holder name"
                        fullWidth
                        error={!!bankAccountErrors.accountHolderName}
                        helperText={bankAccountErrors.accountHolderName?.message}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...registerBankAccount('bankName')}
                        label="Bank name"
                        fullWidth
                        error={!!bankAccountErrors.bankName}
                        helperText={bankAccountErrors.bankName?.message}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...registerBankAccount('accountNumber')}
                        label="Account number"
                        fullWidth
                        error={!!bankAccountErrors.accountNumber}
                        helperText={bankAccountErrors.accountNumber?.message}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...registerBankAccount('ifscCode')}
                        label="IFSC code"
                        fullWidth
                        error={!!bankAccountErrors.ifscCode}
                        helperText={bankAccountErrors.ifscCode?.message}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField {...registerBankAccount('branch')} label="Branch (optional)" fullWidth />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField {...registerBankAccount('upiId')} label="UPI ID (optional)" fullWidth />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={createBankAccount.isPending}
                    sx={{ mt: 2 }}
                  >
                    Save account
                  </Button>
                </Paper>
              )}

              {bankAccountsQuery.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !manageableAccounts.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Typography color="text.secondary">No bank accounts saved yet.</Typography>
                </Paper>
              ) : (
                manageableAccounts.map((account) => (
                  <Paper
                    key={account.id}
                    elevation={0}
                    sx={{
                      p: 2.25,
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.5,
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <CreditCardIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography fontWeight={700}>{account.bankName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {account.accountHolderName} · {account.accountNumberMasked} · {account.ifscCode}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {account.isPrimary && <StatusBadge status="approved" label="Primary" />}
                        <StatusBadge
                          status={account.verificationStatus === 'VERIFIED' ? 'approved' : 'pending'}
                          label={account.verificationStatus}
                        />
                        {!account.isPrimary && (
                          <Button
                            size="small"
                            onClick={() => setPrimaryBankAccount.mutate(account.id)}
                            disabled={setPrimaryBankAccount.isPending}
                          >
                            Set primary
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            const confirmed = window.confirm(
                              `Remove ${account.bankName} ending ${account.accountNumberMasked}?`
                            );
                            if (confirmed) {
                              deleteBankAccount.mutate(account.id);
                            }
                          }}
                          disabled={deleteBankAccount.isPending}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
